import React, { ReactNode, useEffect, useRef } from 'react';
import Icon from 'src/components/ui/Icon/Icon';
import classNames from 'classnames';
import { useFocusTrap } from 'src/hooks/useFocusTrap';
import Portal from '../Portal/Portal';
import styles from './Modal.module.scss';

type ModalProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose?: () => void;
  hasBackdrop?: boolean;
  hasCloseButton?: boolean;
  hasDefaultPadding?: boolean;
  smallSize?: boolean;
  wide?: boolean;
};

const escapeStack: object[] = [];

const Modal: React.FC<ModalProps> = ({
  children,
  isOpen,
  onClose,
  hasBackdrop = true,
  hasCloseButton = true,
  hasDefaultPadding = false,
  smallSize = false,
  wide = false,
}) => {
  const modalRef = useFocusTrap(isOpen);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const escapeToken = useRef({}).current;

  useEffect(() => {
    if (!isOpen) return undefined;
    escapeStack.push(escapeToken);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || escapeStack[escapeStack.length - 1] !== escapeToken) return;
      event.preventDefault();
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      escapeStack.splice(escapeStack.indexOf(escapeToken), 1);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, escapeToken]);

  if (!isOpen) {
    return null;
  }

  return hasBackdrop ? (
    <Portal backdrop>
      <div className={styles.backdrop} role="presentation" onMouseDown={() => onClose?.()}>
        <div
          ref={modalRef}
          className={classNames(styles.root, {
            [styles.defaultPadding]: hasDefaultPadding,
            [styles.wide]: wide,
          })}
          onMouseDown={e => e.stopPropagation()}
          role="presentation">
          {hasCloseButton && (
            <button
              className={classNames(styles.closeButton, {
                '!top-[12px] !right-[12px]': smallSize,
              })}
              onClick={onClose}
              type="button"
              aria-label="Close Modal">
              <Icon name="close" width={20} height={20} className="opacity-60" />
            </button>
          )}

          {children}
        </div>
      </div>
    </Portal>
  ) : (
    <div
      className={classNames(styles.root, {
        [styles.defaultPadding]: hasDefaultPadding,
      })}
      onMouseDown={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
      role="presentation">
      {hasCloseButton && (
        <button
          className={classNames(styles.closeButton, {
            '!top-[12px] !right-[12px]': smallSize,
          })}
          onClick={onClose}
          type="button"
          aria-label="Close Modal">
          <Icon name="close" width={20} height={20} className="opacity-60" />
        </button>
      )}

      {children}
    </div>
  );
};

export default Modal;
